import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  api,
  type ForumThread,
  type ForumReply,
  type ForumThreadDetail,
  type Paginated,
} from "@/lib/api";

export type ForumFilters = {
  q?: string;
  category?: string;
  intent?: string;
  sort?: string;
  filter?: string;
};
export type ForumStats = {
  threads: number;
  replies: number;
  solved: number;
  activeThisWeek: number;
  categories: Array<{ slug: string; threads: number }>;
  bounties: Array<{
    label: string;
    open: number;
    minUsdc: number;
    totalUsdc: number;
    jobId: string | null;
  }>;
};
export type ForumViewer = {
  authorKey: string;
  isModerator: boolean;
  following: boolean;
  helpful: boolean;
  helpfulReplyIds: string[];
};
export type FollowedThread = ForumThread & {
  unread: boolean;
  lastReadAt: string;
};
const retry = (failures: number, error: any) =>
  ![401, 403, 404, 429].includes(error?.response?.status) && failures < 2;

export function useForumIndex(filters: ForumFilters) {
  return useInfiniteQuery<
    Paginated<ForumThread> & {
      meta: { nextCursor: string | null; total: number };
    }
  >({
    queryKey: ["forum", "index", filters],
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) =>
      (
        await api.get("/forum/threads", {
          params: { ...filters, limit: 20, cursor: pageParam },
        })
      ).data,
    getNextPageParam: (page) => page.meta.nextCursor ?? undefined,
    staleTime: 15000,
    retry,
  });
}
export function useForumStats() {
  return useQuery<ForumStats>({
    queryKey: ["forum", "stats"],
    queryFn: async () => (await api.get("/forum/stats")).data.data,
    staleTime: 60000,
    retry,
  });
}
export function useForumDiscussion(slug?: string) {
  return useQuery<ForumThreadDetail>({
    queryKey: ["forum", "thread", slug],
    enabled: !!slug,
    queryFn: async () =>
      (await api.get("/forum/threads/" + encodeURIComponent(slug!))).data.data,
    staleTime: 15000,
    retry,
  });
}
export function useForumReplies(slug?: string) {
  return useInfiniteQuery<Paginated<ForumReply>>({
    queryKey: ["forum", "replies", slug],
    enabled: !!slug,
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) =>
      (
        await api.get(
          "/forum/threads/" + encodeURIComponent(slug!) + "/replies",
          { params: { limit: 20, cursor: pageParam } }
        )
      ).data,
    getNextPageParam: (page) => page.meta.nextCursor ?? undefined,
    staleTime: 15000,
    retry,
  });
}
export function useForumLinkedReply(id: string | null) {
  return useQuery<ForumReply & { thread: { id: string; slug: string } }>({
    queryKey: ["forum", "linked-reply", id],
    enabled: !!id,
    queryFn: async () =>
      (await api.get("/forum/replies/" + encodeURIComponent(id!))).data.data,
    retry,
  });
}
export function useForumViewer(slug?: string) {
  return useQuery<ForumViewer>({
    queryKey: ["forum", "viewer", slug],
    enabled: !!slug,
    queryFn: async () =>
      (await api.get("/forum/threads/" + encodeURIComponent(slug!) + "/viewer"))
        .data.data,
    retry,
  });
}
export function useForumActivity() {
  return useInfiniteQuery<
    Paginated<FollowedThread> & {
      meta: { nextCursor: string | null; unread: number };
    }
  >({
    queryKey: ["forum", "activity"],
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) =>
      (await api.get("/forum/me", { params: { limit: 20, cursor: pageParam } }))
        .data,
    getNextPageParam: (page) => page.meta.nextCursor ?? undefined,
    refetchInterval: 60000,
    retry,
  });
}
export function useForumAction() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: async ({
      method = "post",
      path,
      body,
      key,
    }: {
      method?: "post" | "patch" | "put" | "delete";
      path: string;
      body?: unknown;
      key?: string;
    }) =>
      (
        await api.request({
          method,
          url: "/forum" + path,
          data: body,
          ...(key && { headers: { "Idempotency-Key": key } }),
        })
      ).data.data,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["forum"] }),
  });
}
export function forumError(error: any): string {
  const payload = error?.response?.data;
  if (payload?.errors?.length)
    return payload.errors
      .map((e: any) => [e.field, e.message].filter(Boolean).join(": "))
      .join(". ");
  if (Array.isArray(payload?.message)) return payload.message.join(". ");
  return (
    payload?.message ||
    "Could not reach the forum. Your draft is still here; try again when the connection returns."
  );
}
